import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { formatShortDate } from "@/utils/article";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}

const hotKeywords = ["流行色", "咖啡", "旗舰店", "可持续", "联名", "穿搭"];

export default function SearchOverlay({ open, onClose, initialQuery = "" }: SearchOverlayProps) {
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    if (open) setQuery(initialQuery);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return [];
    return articles
      .filter((item) => {
        const haystack = `${item.title} ${item.summary} ${item.tags.join(" ")} ${item.categoryName}`.toLowerCase();
        return haystack.includes(keyword);
      })
      .slice(0, 8);
  }, [query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-20 md:pt-28">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-background-50 shadow-xl animate-fade-up">
        <div className="flex items-center gap-3 border-b border-background-200 px-4 py-4 md:px-6">
          <i className="ri-search-line text-xl text-foreground-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索品牌动态、生活方式、潮流趋势…"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:text-base"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-4 py-4 md:px-6">
          {query.trim() === "" ? (
            <div>
              <p className="mb-3 text-xs font-medium uppercase tracking-wider text-foreground-400">热门搜索</p>
              <div className="flex flex-wrap gap-2">
                {hotKeywords.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => setQuery(word)}
                    className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length > 0 ? (
            <ul className="divide-y divide-background-200">
              {results.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    onClick={onClose}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-background-100"
                  >
                    <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-900">{item.title}</p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="rounded-full bg-primary-100 px-2 py-0.5 text-primary-700">{item.categoryName}</span>
                        <span>{formatShortDate(item.date)}</span>
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-10 text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-600">
                没有找到与「{query}」相关的内容
              </p>
              <p className="mt-1 text-xs text-foreground-400">换个关键词试试，例如「流行色」「咖啡」「联名」</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}